import { useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';

import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { usePerformanceData } from '@/hooks/usePerformanceData';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { platformsInScope } from '@/lib/performance/types';
import { SubViewSwitcher } from './sub-view-switcher';
import { ColumnsPopover } from '../cells';
import {
  KeywordsTab,
  KEYWORDS_TABLE_COLUMNS,
  KEYWORDS_COLUMN_IDS,
  KEYWORDS_PINNED_COLUMN_IDS,
  KEYWORDS_DEFAULT_VISIBLE_IDS,
} from '../tabs/keywords-tab';
import {
  SearchTermsTab,
  SEARCH_TERMS_TABLE_COLUMNS,
  SEARCH_TERMS_COLUMN_IDS,
  SEARCH_TERMS_PINNED_COLUMN_IDS,
  SEARCH_TERMS_DEFAULT_VISIBLE_IDS,
} from '../tabs/search-terms-tab';
import {
  consolidatedKeywordsSubViewAtom,
  consolidatedPerformanceScopeAtom,
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCustomRangeAtom,
  consolidatedPerformanceGroupByAtom,
  consolidatedPerformanceComparisonAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

// perf-v2 DEFAULT_LAYOUTS.keywords / .search-terms tab ids under the
// consolidated shell's shared widget-layout atom (see CONSOLIDATED_DEFAULT_LAYOUTS).
const GOOGLE_KEYWORDS_LAYOUT_KEY = 'google-keywords';
const GOOGLE_SEARCH_TERMS_LAYOUT_KEY = 'google-search-terms';

/**
 * Consolidated Keywords & Terms tab (#5995) — Google-only (the shell gates this
 * tab off under Meta scope), so there's NO scope branch. Reuses perf-v2's
 * full-depth KeywordsTab + SearchTermsTab (Quality Score, IS metrics, status
 * filtering, column pickers, CSV export) fed by the shared usePerformanceData
 * hook so it honors the toolbar filters (#5995) — this replaces the previous
 * hardcoded 30-day window. Owns the Keywords / Search Terms sub-view toggle +
 * column pickers locally; the chart is deferred (renderChart => null,
 * performance-chart widget omitted from the layout) as on the other tabs.
 */
export function ConsolidatedKeywordsTermsPanel({
  getTabLayout,
  onReorder,
  onResize,
}: {
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  const { symbol } = useCurrencySymbol('google');
  const cs = symbol ?? '';

  // Atom-backed so Overview's "View all" links can pick the sub-view (09-08).
  const [subView, setSubView] = useAtom(consolidatedKeywordsSubViewAtom);
  // Keywords and search terms are Google concepts: mark them whenever the
  // page scope mixes platforms (the audience switcher's rule).
  const scope = useAtomValue(consolidatedPerformanceScopeAtom);
  const markSubViews = platformsInScope(scope).length > 1;

  const {
    visible: visibleKeywordColumns, toggle: toggleKeywordColumn, reset: resetKeywordColumns,
  } = useColumnVisibility({
    settingsKey: 'keywords',
    allIds: KEYWORDS_COLUMN_IDS,
    pinnedIds: KEYWORDS_PINNED_COLUMN_IDS,
    defaultVisibleIds: KEYWORDS_DEFAULT_VISIBLE_IDS,
  });
  const {
    visible: visibleSearchTermColumns, toggle: toggleSearchTermColumn, reset: resetSearchTermColumns,
  } = useColumnVisibility({
    settingsKey: 'search_terms',
    allIds: SEARCH_TERMS_COLUMN_IDS,
    pinnedIds: SEARCH_TERMS_PINNED_COLUMN_IDS,
    defaultVisibleIds: SEARCH_TERMS_DEFAULT_VISIBLE_IDS,
  });

  // Driven by the shared filter toolbar (#5971/#5995).
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const groupBy = useAtomValue(consolidatedPerformanceGroupByAtom);
  const comparison = useAtomValue(consolidatedPerformanceComparisonAtom);
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);

  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);


  // Toggle the hook's `tab` by sub-view so the search-terms query only fires
  // when that view is active (it's gated on tab === 'search-terms' | 'overview').
  const perf = usePerformanceData({
    includeExternal,
    tab: subView === 'search-terms' ? 'search-terms' : 'keywords',
    campaignSubView: 'by-type',
    comparisonMode: comparison,
    selectedCampaignIds,
    chartDateRange: selectedDays,
    chartGroupBy: groupBy,
    showUserEdits: false,
    selectedMetric: 'conversions',
    selectedDays,
    customRange,
    typeFilter,
    cs,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <SubViewSwitcher
          items={[
            { id: 'keywords' as const, label: 'Keywords', platform: markSubViews ? ('google' as const) : undefined },
            { id: 'search-terms' as const, label: 'Search Terms', platform: markSubViews ? ('google' as const) : undefined },
          ]}
          active={subView}
          onChange={setSubView}
        />
        {/* Right-aligned like every table toolbar control (ruling 09-08). */}
        <div className="ml-auto">
          {subView === 'keywords' && (
            <ColumnsPopover
              columnsMeta={KEYWORDS_TABLE_COLUMNS}
              visibleIds={visibleKeywordColumns}
              totalIds={KEYWORDS_COLUMN_IDS}
              onToggle={toggleKeywordColumn}
              onReset={resetKeywordColumns}
            />
          )}
          {subView === 'search-terms' && (
            <ColumnsPopover
              columnsMeta={SEARCH_TERMS_TABLE_COLUMNS}
              visibleIds={visibleSearchTermColumns}
              totalIds={SEARCH_TERMS_COLUMN_IDS}
              onToggle={toggleSearchTermColumn}
              onReset={resetSearchTermColumns}
            />
          )}
        </div>
      </div>
      {subView === 'keywords' ? (
        <KeywordsTab
          campaignIds={perf.campaignIds || undefined}
          visibleColumns={visibleKeywordColumns}
          currencySymbol={cs}
          dateRangeLabel={perf.dateRangeLabel}
          dateRangeFilter={perf.dateRangeFilter}
          layout={getTabLayout(GOOGLE_KEYWORDS_LAYOUT_KEY)}
          onReorder={(l) => onReorder(GOOGLE_KEYWORDS_LAYOUT_KEY, l)}
          onResize={(id, w) => onResize(GOOGLE_KEYWORDS_LAYOUT_KEY, id, w)}
          renderChart={() => null}
          activeCampaigns={perf.activeCampaigns}
        />
      ) : (
        <SearchTermsTab
          terms={perf.searchTermsData}
          isLoading={perf.isSearchTermsLoading}
          visibleColumns={visibleSearchTermColumns}
          currencySymbol={cs}
          dateRangeLabel={perf.dateRangeLabel}
          dateRangeFilter={perf.dateRangeFilter}
          layout={getTabLayout(GOOGLE_SEARCH_TERMS_LAYOUT_KEY)}
          onReorder={(l) => onReorder(GOOGLE_SEARCH_TERMS_LAYOUT_KEY, l)}
          onResize={(id, w) => onResize(GOOGLE_SEARCH_TERMS_LAYOUT_KEY, id, w)}
        />
      )}
    </div>
  );
}
